import { Suspense, useEffect, useRef } from "react";
import { Outlet, useLocation, useNavigationType } from "react-router-dom";
import { TopBar } from "./top-bar";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { ErrorBoundary } from "@/components/error-boundary";
import { PageSpinner } from "@/components/ui/spinner";
import { ApiStatusBanner } from "@/components/api-status-banner";
import { EnvironmentBanner } from "@/components/environment-banner";
import { BlueprintBackdrop, BlueprintDensityProvider } from "@/components/blueprint-backdrop";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QuickAddFab } from "@/components/quick-add-button";
import { CallDock } from "@/components/call-dock";
import { SoftphoneHost } from "@/lib/use-softphone";
import { QuickBookProvider } from "@/lib/quick-book-context";
import { QuickBookPanel } from "@/components/quick-book-panel";
import { CallPanelProvider } from "@/lib/call-panel-context";
import { FailProtocolAlertBanner } from "@/components/fail-protocol-alert-banner";
import { PriceCheckHost } from "@/components/price-check";
import { TaskFlyoutHost } from "@/components/task-flyout-host";
import { UniversalDropLayer } from "@/components/universal-drop-layer";
import { TechLocationReporter } from "@/components/tech-location-reporter";
import { PullToRefresh } from "@/components/pull-to-refresh";
import { useAuth } from "@/lib/auth";
import { LeftOpenProvider } from "@/components/left-open/left-open-provider";
import { TeachProvider } from "@/teach/teach-state";
import { WhatsNewAutoOpen } from "@/components/whats-new/auto-open";
import { useUiDensity, DENSITY_WIDTH_CLASS } from "@/lib/use-ui-density";
import { cn } from "@/lib/utils";

/** No job left open: the leave question runs on every screen for a tech with
 *  the switch on (inert for everyone else), so it wraps the whole shell.
 *  Teach cards (#1838): one person's seen/used state, inert while the switch
 *  is off. "What's new" notes (migration 659) open by themselves once. */
export function AppShell() {
    return (
        <LeftOpenProvider>
            <TeachProvider>
                <AppShellInner />
                {/* "What's new" notes open by themselves once, on the next app open. */}
                <WhatsNewAutoOpen />
            </TeachProvider>
        </LeftOpenProvider>
    );
}

function AppShellInner() {
    const { isField, isOffice, isAdmin } = useAuth();
    const techOnly = isField && !isOffice && !isAdmin;
    const { density } = useUiDensity();

    // Scroll discipline (G5): forward navigations start every page at the
    // top, but browser Back/Forward (POP) restores the scroll position the
    // page was left at — `<main>` is the scroll container (not the window),
    // so the browser's native restoration can't do this for us. Keyed on
    // pathname so in-page tab/filter changes (which only touch the query
    // string) don't yank the user around.
    const mainRef = useRef<HTMLElement>(null);
    const scrollPositions = useRef(new Map<string, number>());
    const { pathname } = useLocation();
    const navigationType = useNavigationType();

    // Record where each page is scrolled AS the user scrolls — reading
    // scrollTop at navigation time is too late (the outgoing page's DOM is
    // already replaced and the offset clamped by then).
    const pathnameRef = useRef(pathname);
    const restoringRef = useRef(false);
    useEffect(() => {
        const el = mainRef.current;
        if (!el) return;
        const onScroll = () => {
            if (!restoringRef.current) scrollPositions.current.set(pathnameRef.current, el.scrollTop);
        };
        el.addEventListener("scroll", onScroll, { passive: true });
        return () => el.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        pathnameRef.current = pathname;
        const el = mainRef.current;
        let cancelled = false;
        if (navigationType === "POP") {
            const saved = scrollPositions.current.get(pathname) ?? 0;
            // The page's data usually hasn't rendered yet (spinner), so a few
            // retries let the content grow tall enough to take the offset.
            restoringRef.current = true;
            let attempts = 0;
            const restore = () => {
                if (cancelled || !el) return;
                el.scrollTop = saved;
                if (Math.abs(el.scrollTop - saved) > 1 && attempts < 20) {
                    attempts += 1;
                    setTimeout(restore, 100);
                } else {
                    restoringRef.current = false;
                }
            };
            restore();
        } else {
            el?.scrollTo({ top: 0, left: 0 });
            window.scrollTo({ top: 0, left: 0 });
        }
        return () => {
            cancelled = true;
            restoringRef.current = false;
        };
    }, [pathname, navigationType]);

    return (
        <TooltipProvider delayDuration={300}>
            <BlueprintDensityProvider>
                <QuickBookProvider>
                    <CallPanelProvider>
                        <div className="flex flex-col h-screen overflow-hidden">
                            {/* SKIP LINK (#1205) — first thing in the tab order.
                                The toolbar is 17 controls deep before the page
                                begins; without this, reaching the content by
                                keyboard means tabbing through every one of them
                                on every navigation. Invisible until focused. */}
                            <a
                                href="#main-content"
                                className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-[100] focus:rounded-lg focus:bg-foreground focus:px-4 focus:py-2.5 focus:text-sm focus:font-bold focus:text-card focus:shadow-lg focus:outline-2 focus:outline-offset-2"
                            >
                                Skip to main content
                            </a>
                            <BlueprintBackdrop />
                            <PullToRefresh />
                            {/* WHICH BOX AM I ON — first thing in the column, so it
                                can't be scrolled out of frame. Renders nothing on
                                production; the sandbox is a deliberate look-alike and
                                this is the only thing that tells them apart (#1052). */}
                            <EnvironmentBanner />
                            {/* Opt-in field GPS reporter (live-GPS spike). Renders nothing; */}
                            {/* technician-only + gated on the `field.gpsEnabled` flag (default OFF). */}
                            {techOnly && <TechLocationReporter />}
                            {/* Missed jobs are surfaced non-blockingly by the MissedJobsChip
                        in the office top bar (a persistent count + slide-over panel +
                        once-per-session toast) — no full-screen gate on every load. */}
                            <TopBar />
                            {/* ORG-WIDE FAIL ALERT — every office user, every page,
                        until the protocol quote is sent. Renders nothing for
                        techs (finance.view gate inside the component). */}
                            {!techOnly && <FailProtocolAlertBanner />}
                            {/* Work Pulse retired — its counts moved into the v3 toolbar's
                        calm pipeline strip (TOOLBAR-SPEC.md, row 2). */}
                            <main id="main-content" tabIndex={-1} ref={mainRef} className="flex-1 overflow-y-auto">
                                {/* Extra bottom padding below lg clears the floating
                            QuickAdd FAB (lg:hidden) so the last row/button is
                            never trapped behind it on phones/tablets. */}
                                <div
                                    className={cn(
                                        "px-4 pt-5 pb-24 lg:pb-5 sm:px-6 lg:px-8 space-y-4 mx-auto w-full",
                                        DENSITY_WIDTH_CLASS[density],
                                    )}
                                >
                                    {!techOnly && <Breadcrumbs />}
                                    <ApiStatusBanner />
                                    <ErrorBoundary>
                                        {/* Single Suspense boundary for every lazy()-d route mounted
                                    inside the shell. Each lazy chunk shows the same page-level
                                    spinner during fetch, then the route renders normally. */}
                                        <Suspense fallback={<PageSpinner />}>
                                            <Outlet />
                                        </Suspense>
                                    </ErrorBoundary>
                                </div>
                            </main>
                            {!techOnly && <QuickAddFab />}
                            {!techOnly && <CallDock />}
                            {/* GWC Voice softphone lifecycle — renders nothing; stays dark
                                until the Twilio Voice env is configured (token 503s). */}
                            {!techOnly && <SoftphoneHost />}
                            <QuickBookPanel />
                            {/* PRICE CHECK popover — office-permission gated inside
                        the host (renders nothing for techs). */}
                            <PriceCheckHost />
                            {/* The task drawer that opens OVER whatever page you are
                        on ("Do it now" on a job, a task in the toolbar list). */}
                            <TaskFlyoutHost />
                            {/* UNIVERSAL DROP LAYER — drop a report/WO on ANY page and
                        it's routed by what it is (COMMAND-CENTER-REDESIGN.md
                        decision 5). Office/admin only; techs use the field flag
                        flow. Supersedes the per-page PageDropZone. */}
                            {!techOnly && <UniversalDropLayer />}
                        </div>
                    </CallPanelProvider>
                </QuickBookProvider>
            </BlueprintDensityProvider>
        </TooltipProvider>
    );
}
